import { Box, Typography } from '@mui/material';
import Image from 'next/image';
import { FC, useMemo } from 'react';
import { useDataActions } from '@/hooks/useDataState';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useTree } from '@/hooks/useTree';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { joinApiEndpointPath } from '@/lib/utils/urlUtils';

interface InlineImagesProps {
  title?: string;
}

const InlineImages: FC<InlineImagesProps> = ({ title = 'Images' }) => {
  const { getFiles } = useTree();
  const { selectedNode } = useTreeSelection();
  const systemConfig = useSystemConfig();
  // Subscribe to specific loading state for better performance
  const { isLoadingFiles } = useDataActions(selectedNode?.dataId);

  const isReadOnlyNode = selectedNode ? nodeIsReadOnlyForUi(selectedNode.type) : false;

  const images = useMemo(() => {
    const fileMetaDataArray = selectedNode && !isLoadingFiles ? (getFiles(selectedNode.dataId) ?? []) : [];
    const files = fileMetaDataArray.flatMap((f) => f.files?.map((f) => f)).filter((f) => !!f) || [];
    // The server now adds an imageUrl which allows inlining. If there is none, it means the file is not an image.
    return files.filter((file) => !!file.imageUrl);
  }, [selectedNode, isLoadingFiles, getFiles]);

  // Don't render anything if there are no images
  if (images.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {title}:
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {images.map((file, fileIdx) => {
          const fileBase = systemConfig?.apiEndpoint;
          const downloadHref = joinApiEndpointPath(fileBase, file.downloadUrl) || '#';
          const imageSrc = joinApiEndpointPath(fileBase, file.imageUrl);
          return (
            <a key={`${fileIdx}`} href={downloadHref} target="_blank" rel="noopener noreferrer">
              <Image
                src={imageSrc}
                alt={file.filename || `Image ${fileIdx + 1}`}
                width={300}
                height={200}
                style={{
                  borderRadius: 8,
                  objectFit: 'contain',
                  border: '1px solid #ddd',
                  cursor: 'pointer',
                }}
                unoptimized // Stupid next doesn't want to work with images from an api endpoint.
              />
            </a>
          );
        })}
      </Box>
      {!isReadOnlyNode ? (
        <Typography
          variant="body1"
          sx={{
            fontStyle: 'italic',
            textDecoration: 'underline',
            mt: 1,
            color: 'primary.secondary',
          }}
        >
          Go to Files to modify
        </Typography>
      ) : null}
    </Box>
  );
};

export default InlineImages;
